import { useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../lib/AuthContext';
import { api } from '../../lib/api';
import { Download, FileDown, FileSpreadsheet, Loader2, MessageSquare, Printer, Settings2, X } from 'lucide-react';
import { QuoteDocumentView } from '../../components/QuoteDocumentView';
import { ScenarioComparePanel } from '../../components/project-detail/ScenarioComparePanel';
import { Tooltip } from '../../components/ui/Tooltip';
import type { ProjectDetailOutletContext } from './outletTypes';

export function RapportageTab() {
  const v = useOutletContext<ProjectDetailOutletContext>();
  const { user } = useAuth();
  const blurQuote = user?.tenant_billing?.blur_previous_outputs ?? false;
  const [feedbackNotes, setFeedbackNotes] = useState('');
  const [feedbackCategory, setFeedbackCategory] = useState('algemeen');
  const [feedbackBusy, setFeedbackBusy] = useState(false);
  const [feedbackMsg, setFeedbackMsg] = useState<string | null>(null);
  const [feedbackErr, setFeedbackErr] = useState<string | null>(null);

  const { data: catalogMeta } = useQuery({
    queryKey: ['catalog-meta', v.tenantIdForApi],
    queryFn: () => api.getCatalogMeta(v.tenantIdForApi),
    staleTime: 60_000,
  });

  if (!v.hasSystems) {
    return (
      <div
        className="flex flex-col items-center rounded-xl border border-dashed border-border bg-surface-card/80 px-6 py-10 text-center"
        role="status"
      >
        <FileDown className="mb-4 h-12 w-12 text-text-muted/40" aria-hidden />
        <h2 className="mb-2 text-lg font-semibold text-text">Nog geen export</h2>
        <p className="mb-4 max-w-md text-sm text-text-muted">
          Sla eerst regels op bij Posten. Daarna worden Resultaat en Rapportage actief voor totalen, offerte en
          downloads.
        </p>
        <Link to={`/projects/${v.projectId}/posten`} className="font-medium text-primary hover:underline">
          Naar posten →
        </Link>
      </div>
    );
  }

  if (v.aggregationLoading) {
    return (
      <div
        className="flex flex-col items-center gap-3 rounded-xl border border-border bg-surface-card px-4 py-12 text-sm text-text-muted"
        role="status"
        aria-live="polite"
      >
        <Loader2 className="h-8 w-8 animate-spin text-primary" aria-hidden />
        <p>Berekening laden voor export…</p>
        <p className="max-w-sm text-center text-xs">
          Zodra de totalen klaar zijn, kun je hier offertes en downloads gebruiken.
        </p>
      </div>
    );
  }

  if (v.aggregationError) {
    return (
      <div
        className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200"
        role="alert"
      >
        <strong className="block">Export niet mogelijk</strong>
        <p className="mt-2">{v.aggregationError}</p>
        <p className="mt-3 text-xs opacity-90">
          Los eerst de fout op de <strong>Resultaat</strong>-tab of pas je posten aan.
        </p>
        <div className="mt-4 flex flex-wrap gap-3">
          <Link
            to={`/projects/${v.projectId}/resultaat`}
            className="font-medium text-red-900 underline dark:text-red-100"
          >
            Naar resultaat →
          </Link>
          <Link to={`/projects/${v.projectId}/posten`} className="font-medium text-red-900 underline dark:text-red-100">
            Naar posten →
          </Link>
        </div>
      </div>
    );
  }

  if (!v.aggregation) {
    return (
      <div className="rounded-xl border border-border bg-surface-card p-8 text-center text-sm text-text-muted" role="status">
        <h2 className="mb-2 text-lg font-semibold text-text">Geen berekening beschikbaar</h2>
        <p className="mb-4 max-w-md mx-auto">
          Open tab Resultaat om de doorrekening te laden; daarna kun je hier exporteren.
        </p>
        <Link to={`/projects/${v.projectId}/resultaat`} className="font-medium text-primary hover:underline">
          Naar resultaat →
        </Link>
      </div>
    );
  }

  return (
    <>
      {v.exportFeedback && (
        <div
          className="mb-4 flex items-start justify-between gap-3 rounded-xl border border-emerald-200/90 bg-emerald-50/95 px-4 py-3 text-sm text-emerald-950 dark:border-emerald-800 dark:bg-emerald-950/35 dark:text-emerald-50"
          role="status"
        >
          <p className="font-medium">{v.exportFeedback}</p>
          <button
            type="button"
            className="shrink-0 rounded-lg p-1 text-emerald-800 hover:bg-emerald-100 dark:text-emerald-100 dark:hover:bg-emerald-900/50"
            aria-label="Melding sluiten"
            onClick={() => v.setExportFeedback(null)}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="mb-6 rounded-xl border border-border bg-surface-card p-5 shadow-sm">
        <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-lg font-semibold text-text">Rapportage &amp; export</h2>
            <p className="mt-1 text-sm text-text-muted">
              Kies vooraf in <strong className="text-text">Exportinstellingen</strong> hoe uitgebreid je calculatie-export
              is: kostenopbouw, regeldetail (soort/bouwdeel/systeem), per-deel, CO₂, voorbehoud en CSV-vorm. Zelfde
              totalen als Resultaat; presentatie stuur je zelf. Hieronder:{' '}
              <strong className="text-text">calculatiebestanden</strong>, daarna{' '}
              <strong className="text-text">offerte</strong> (preview en print). Uitleg:{' '}
              <Link to="/help/export" className="font-medium text-primary hover:underline">
                Export &amp; offerte
              </Link>
              .
            </p>
            {catalogMeta?.catalog_version && (
              <p className="mt-2 text-xs text-text-muted">
                <span className="font-medium text-text">Bibliotheekversie (catalogus):</span> {catalogMeta.catalog_version}
                {catalogMeta.catalog_source ? ` · bron: ${catalogMeta.catalog_source}` : ''}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              type="button"
              onClick={() => v.setExportModalOpen(true)}
              className={`${v.btnSecondary} gap-2`}
            >
              <Settings2 className="h-4 w-4" aria-hidden />
              Exportinstellingen
            </button>
            <Tooltip
              iconOnly
              label="Exportinstellingen"
              content="BTW, modelcorrectie, CSV-scheidingsteken, kostenopbouw, regeldetail, CO₂, voorbehoud, per-deel, offerte-PDF."
            />
          </div>
        </div>
        <div className="mb-4 space-y-1 rounded-lg border border-border/70 bg-surface px-3 py-2 text-xs text-text-muted">
          <p>
            <span className="font-medium text-text">Actief: </span>
            BTW {(v.exportVatRate * 100).toFixed(0)}% · modelcorrectie ×{v.exportWasteFactor.toFixed(2)} · CSV{' '}
            {v.exportCsvDelimiter === ';' ? 'puntkomma (;)' : 'komma (,)'} ·{' '}
            {v.exportIncludeCostSummary ? 'met kostenopbouw' : 'zonder kostenopbouw'} ·{' '}
            {v.exportIncludeRegeldetail ? 'met regeldetail' : 'compacte regels'} ·{' '}
            {v.exportIncludeCo2 ? 'met CO₂' : 'zonder CO₂'} ·{' '}
            {v.exportIncludeDisclaimer ? 'met voorbehoud' : 'zonder voorbehoud'} · begroting{' '}
            {v.exportIncludePerDeelBegroting ? 'met per-deel' : 'zonder per-deel'} —{' '}
            <button
              type="button"
              className="font-medium text-primary hover:underline"
              onClick={() => v.setExportModalOpen(true)}
            >
              wijzig
            </button>
          </p>
        </div>
        <div className="mb-4 rounded-lg border border-amber-200/80 bg-amber-50/90 px-3 py-2.5 text-xs leading-relaxed text-text-muted dark:border-amber-900/50 dark:bg-amber-950/30 dark:text-amber-100/90">
          <strong className="text-text">Model en voorbehoud:</strong> export en offertes zijn gebaseerd op de ingevoerde
          posten en het prijsmodel in CalqTool™. Uitkomsten zijn <strong className="text-text">indicatief</strong> en
          niet bindend, tenzij u dat contractueel anders afspreekt. Gebruik de voorbehoudregel in de exportinstellingen
          waar passend. Zie ook de{' '}
          <Link to="/terms" className="font-medium text-primary hover:underline">
            algemene voorwaarden
          </Link>
          .
        </div>

        <section className="mt-6 border-t border-border pt-5" aria-labelledby="rapportage-calculatie-title">
          <h3 id="rapportage-calculatie-title" className="text-base font-semibold text-text">
            Calculatie-export
          </h3>
          <p className="mt-1 text-sm text-text-muted">
            CSV, Excel-vriendelijke CSV (UTF-8 met BOM) en begroting-PDF met tabellen en — indien gekozen — kostenopbouw
            en kolommen voor soort, bouwdeel en systeem. Zelfde totalen als Resultaat; bestandsnamen{' '}
            <code className="rounded bg-surface px-1 text-xs">calculatie_*</code>.
          </p>
          <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
            <button
              type="button"
              onClick={() => void v.handleExport('csv')}
              disabled={v.exporting}
              className={v.btnSecondary}
            >
              <Download className="h-4 w-4" aria-hidden />
              {v.exporting ? 'Bezig…' : 'Download Calculatie CSV'}
            </button>
            <button
              type="button"
              onClick={() => void v.handleExport('excel')}
              disabled={v.exporting}
              title="UTF-8 met BOM — direct openen in Excel"
              className={v.btnSecondary}
            >
              <FileSpreadsheet className="h-4 w-4" aria-hidden />
              {v.exporting ? 'Bezig…' : 'Download Calculatie Excel'}
            </button>
            <button
              type="button"
              onClick={() => void v.handleExport('pdf')}
              disabled={v.exporting}
              className={v.btnSecondary}
            >
              <FileDown className="h-4 w-4" aria-hidden />
              {v.exporting ? 'Bezig…' : 'Download Calculatie PDF'}
            </button>
          </div>
        </section>

        <section className="mt-6 border-t border-border pt-5" aria-labelledby="rapportage-offerte-title">
          <h3 id="rapportage-offerte-title" className="text-base font-semibold text-text">
            Offerte (preview &amp; print)
          </h3>
          <p className="mt-1 text-sm text-text-muted">
            Klantgerichte weergave op basis van dezelfde posten. <strong className="text-text">Print</strong> volgt na de
            preview. Server-PDF staat onder Exportinstellingen.
          </p>
          <div className="mt-4">
            <button
              type="button"
              onClick={() => void v.handleGenerateDocument()}
              disabled={v.loadingDoc}
              className={v.btnPrimaryLg}
            >
              {v.loadingDoc ? 'Bezig…' : 'Offerte genereren (preview)'}
            </button>
          </div>
        </section>

        <ScenarioComparePanel
          projectId={v.projectId}
          tenantId={v.tenantIdForApi}
          projectType={v.proj.project_type ?? 'renovatie'}
          buildingType={v.proj.building_type ?? 'woning'}
          indexYear={v.indexForPricing}
          regionId={v.proj.context?.region_id || undefined}
          systems={v.proj.systems ?? []}
          inputClass={v.inputClass}
          btnSecondary={v.btnSecondary}
        />

        <section
          className="mt-8 rounded-xl border border-border/80 bg-surface-card/60 p-5"
          aria-labelledby="rapportage-feedback-title"
        >
          <h3 id="rapportage-feedback-title" className="flex items-center gap-2 text-base font-semibold text-text">
            <MessageSquare className="h-5 w-5 text-primary" aria-hidden />
            Feedback
          </h3>
          <p className="mt-1 text-sm text-text-muted">
            Korte opmerking over dit dossier, de export of de tool — wordt intern opgeslagen ter verbetering van het
            model en de gebruikerservaring.
          </p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-text-muted">Onderwerp</label>
              <select
                value={feedbackCategory}
                onChange={(e) => setFeedbackCategory(e.target.value)}
                className={v.inputClass}
              >
                <option value="algemeen">Algemeen</option>
                <option value="export_offerte">Export / offerte</option>
                <option value="scenario">Scenariovergelijking</option>
                <option value="prijs">Prijzen / onderbouwing</option>
                <option value="anders">Anders</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="mb-1 block text-xs font-medium text-text-muted">Je bericht</label>
              <textarea
                value={feedbackNotes}
                onChange={(e) => setFeedbackNotes(e.target.value)}
                rows={4}
                maxLength={4000}
                placeholder="Bijv. export mist een kolom, of uitleg is onduidelijk…"
                className={`${v.inputClass} w-full`}
              />
            </div>
          </div>
          {feedbackErr && (
            <p className="mt-2 text-sm text-red-700 dark:text-red-300" role="alert">
              {feedbackErr}
            </p>
          )}
          {feedbackMsg && (
            <p className="mt-2 text-sm text-emerald-800 dark:text-emerald-200" role="status">
              {feedbackMsg}
            </p>
          )}
          <button
            type="button"
            disabled={feedbackBusy}
            className={`${v.btnSecondary} mt-3`}
            onClick={() => {
              const t = feedbackNotes.trim();
              if (!t) {
                setFeedbackErr('Vul een korte toelichting in.');
                setFeedbackMsg(null);
                return;
              }
              setFeedbackBusy(true);
              setFeedbackErr(null);
              setFeedbackMsg(null);
              void api
                .submitFeedback({
                  project_id: v.projectId,
                  tenant_id: v.tenantIdForApi,
                  notes: t,
                  category_of_deviation: feedbackCategory,
                })
                .then(() => {
                  setFeedbackMsg('Bedankt — je feedback is verstuurd.');
                  setFeedbackNotes('');
                })
                .catch((e) => setFeedbackErr(e instanceof Error ? e.message : 'Versturen mislukt'))
                .finally(() => setFeedbackBusy(false));
            }}
          >
            {feedbackBusy ? 'Versturen…' : 'Feedback versturen'}
          </button>
        </section>
      </div>

      {v.generatedDoc && (
        <div className="mb-6 space-y-3 rounded-xl border border-border bg-surface-card p-4 shadow-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-sm font-medium text-text">Gegenereerde offerte</p>
            <button
              type="button"
              onClick={() => v.handlePrintOfferte()}
              className={`${v.btnSecondary} print:hidden gap-2 text-sm`}
            >
              <Printer className="h-4 w-4" aria-hidden />
              Print / PDF (browser)
            </button>
          </div>
          <p className="text-xs text-text-muted">
            <strong className="text-text">Print</strong> opent het offerteblok voor een schone PDF via je browser. Een
            server-PDF van dezelfde inhoud vind je onder <strong className="text-text">Exportinstellingen</strong>.
          </p>
          <div className={blurQuote ? 'relative' : undefined}>
            {blurQuote && (
              <p className="mb-2 rounded-md border border-amber-200/80 bg-amber-50/90 px-2 py-1.5 text-xs text-amber-950 dark:border-amber-900/50 dark:bg-amber-950/40 dark:text-amber-100">
                <strong>Proefperiode:</strong> vanaf meerdere exports wordt de offerte-preview extra wazig getoond (Fase 9).
                Licentie op factuur? Neem contact op — dan schakelen we dit uit.
              </p>
            )}
            <div className={blurQuote ? 'pointer-events-none select-none blur-[3px]' : undefined} aria-hidden={blurQuote}>
              <QuoteDocumentView doc={v.generatedDoc} onPrint={v.handlePrintOfferte} />
            </div>
            {blurQuote && (
              <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-gradient-to-t from-surface-card/90 to-transparent" />
            )}
          </div>
        </div>
      )}

      {v.exportModalOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-end justify-center p-4 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-labelledby="export-modal-title"
        >
          <button
            type="button"
            className="absolute inset-0 bg-black/45 backdrop-blur-[1px]"
            aria-label="Sluit dialoog"
            onClick={() => v.setExportModalOpen(false)}
          />
          <div className="relative z-10 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-xl border border-border bg-surface-card p-5 shadow-xl">
            <h3 id="export-modal-title" className="text-lg font-semibold text-text">
              Exportinstellingen
            </h3>
            <p className="mt-1 text-sm text-text-muted">
              Deze waarden gelden voor offerte (preview + server-PDF), begroting-PDF en CSV/Excel. Stel eerst in wat je
              wilt meenemen; daarna download je buiten deze modal.
            </p>

            <ol className="mt-4 flex flex-wrap gap-2 text-xs font-medium text-text-muted" aria-label="Stappen export">
              <li className="rounded-full bg-primary/15 px-3 py-1 text-primary">1 · Belasting &amp; model</li>
              <li className="rounded-full bg-surface px-3 py-1">2 · Inhoud export</li>
              <li className="rounded-full bg-surface px-3 py-1">3 · Download</li>
            </ol>

            <div className="mt-4 rounded-lg border border-border/80 bg-surface px-3 py-3 text-xs text-text-muted">
              <p className="font-semibold text-text">Calculatie-export (doel: werkbestand &amp; controle)</p>
              <ul className="mt-2 list-inside list-disc space-y-1">
                <li>
                  <strong className="text-text">CSV / Excel</strong> — optioneel kostenopbouw bovenaan; regels compact of
                  met soort (materiaal/arbeid/materieel), bouwdeel en systeem-id.
                </li>
                <li>
                  <strong className="text-text">Begroting-PDF</strong> — tabellen, totalen, optioneel samenvatting en
                  per-deel; voorbehoud aan/uit.
                </li>
              </ul>
              <p className="mt-2 font-semibold text-text">Offerte (doel: klant)</p>
              <p className="mt-1">Zelfde posten en BTW/modelcorrectie; andere presentatie (preview, print, server-PDF).</p>
            </div>

            <div className="mt-5 space-y-4 border-t border-border pt-4">
              <div>
                <label className="mb-2 block text-sm font-medium text-text">BTW-tarief</label>
                <select
                  className={v.inputClass}
                  value={String(v.exportVatRate)}
                  onChange={(e) => v.setExportVatRate(Number(e.target.value))}
                >
                  <option value="0.21">21% (standaard)</option>
                  <option value="0.09">9%</option>
                  <option value="0">0% (verlegd / vrijgesteld — controleer zelf)</option>
                </select>
              </div>

              {v.customerUxProfile.rapportageShowModelCorrectionHint && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-text">Modelcorrectie (meerwerk / verspilling)</label>
                  <input
                    type="number"
                    min={1}
                    max={1.25}
                    step={0.01}
                    className={v.inputClass}
                    value={v.exportWasteFactor}
                    onChange={(e) => v.setExportWasteFactor(Number(e.target.value) || 1.05)}
                  />
                  <p className="mt-1.5 text-xs text-text-muted">
                    Factor op aggregatie (standaard 1,05). Hoger = voorzichtiger totaal; gelijk voor offerte en
                    begroting in deze sessie.
                  </p>
                </div>
              )}

              <div>
                <label className="mb-2 block text-sm font-medium text-text">CSV-scheidingsteken</label>
                <select
                  className={v.inputClass}
                  value={v.exportCsvDelimiter}
                  onChange={(e) => v.setExportCsvDelimiter(e.target.value as ';' | ',')}
                >
                  <option value=";">Puntkomma (;) — standaard NL / Excel</option>
                  <option value=",">Komma (,) — internationale tools</option>
                </select>
              </div>

              <label className="flex cursor-pointer items-start gap-2 text-sm text-text">
                <input
                  type="checkbox"
                  className="mt-1 rounded border-border"
                  checked={v.exportIncludeCostSummary}
                  onChange={(e) => v.setExportIncludeCostSummary(e.target.checked)}
                />
                <span>
                  <span className="font-medium">Kostenopbouw (samenvatting)</span> — materialen, arbeid, materieel,
                  subtotaal, modelcorrectie en totaal bovenaan CSV/Excel en in begroting-PDF (aanbevolen voor controle).
                </span>
              </label>

              <label className="flex cursor-pointer items-start gap-2 text-sm text-text">
                <input
                  type="checkbox"
                  className="mt-1 rounded border-border"
                  checked={v.exportIncludeRegeldetail}
                  onChange={(e) => v.setExportIncludeRegeldetail(e.target.checked)}
                />
                <span>
                  <span className="font-medium">Uitgebreide regeldetail</span> — kolommen soort, bouwdeel en systeem-id in
                  CSV/Excel; extra kolommen in begroting-PDF. Uit voor eenvoudige spreadsheets; aan voor audit en werkvoorbereiding.
                </span>
              </label>

              <label className="flex cursor-pointer items-start gap-2 text-sm text-text">
                <input
                  type="checkbox"
                  className="mt-1 rounded border-border"
                  checked={v.exportIncludeCo2}
                  onChange={(e) => v.setExportIncludeCo2(e.target.checked)}
                />
                <span>
                  <span className="font-medium">CO₂-indicatie</span> in de samenvatting (alleen als het model CO₂ heeft).
                </span>
              </label>

              <label className="flex cursor-pointer items-start gap-2 text-sm text-text">
                <input
                  type="checkbox"
                  className="mt-1 rounded border-border"
                  checked={v.exportIncludeDisclaimer}
                  onChange={(e) => v.setExportIncludeDisclaimer(e.target.checked)}
                />
                <span>
                  <span className="font-medium">Juridische voorbehoudregel</span> onderaan CSV/Excel en begroting-PDF
                  (aanbevolen).
                </span>
              </label>

              <label className="flex cursor-pointer items-start gap-2 text-sm text-text">
                <input
                  type="checkbox"
                  className="mt-1 rounded border-border"
                  checked={v.exportIncludePerDeelBegroting}
                  onChange={(e) => v.setExportIncludePerDeelBegroting(e.target.checked)}
                />
                <span>
                  <span className="font-medium">Per-deel totalen</span> in begroting-PDF en platte tekst (clusters /
                  disciplines).
                </span>
              </label>

              <div className="rounded-lg border border-border/80 bg-surface px-3 py-3 text-xs text-text-muted">
                <p className="font-semibold text-text">Calculatie-export</p>
                <p className="mt-1">
                  Alle downloads op deze tab gebruiken bovenstaande schakelaars. Bestandsnamen beginnen met{' '}
                  <code className="rounded bg-surface-card px-1">calculatie_</code>. Op de PDF staat ook de dossiernaam als
                  die is ingevuld.
                </p>
              </div>

              <div className="rounded-lg border border-primary/25 bg-primary/[0.06] px-3 py-3">
                <p className="text-sm font-semibold text-text">Offerte als PDF (server)</p>
                <p className="mt-1 text-xs text-text-muted">
                  Zelfde inhoud als <strong className="text-text">Offerte genereren</strong>. Werkt ook zonder eerst de
                  preview te laden.
                </p>
                <button
                  type="button"
                  onClick={() => void v.handleGenerateDocumentPdf()}
                  disabled={v.loadingDocPdf || v.exporting}
                  className={`${v.btnSecondary} mt-3 gap-2`}
                >
                  <FileDown className="h-4 w-4" aria-hidden />
                  {v.loadingDocPdf ? 'Bezig…' : 'Download offerte-PDF'}
                </button>
              </div>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button type="button" className={v.btnSecondary} onClick={() => v.setExportModalOpen(false)}>
                Sluiten
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
